<template>
  <div
    class="order-confirmed"
    :class="{'not-tracking-popup' : !isTrackingPopup}"
  >
    <div
      class="order-confirmed__header"
      :class="{
        'order-confirmed__header-completed':
          bringgOrderResponse.status === 'Complete',
        'order-confirmed__header-margin': $route.name === 'confirmed',
      }"
    >
      <order-confirmation-check class="order-confirmed__header__image" />
      <h1 class="order-confirmed__header__text">
        Order #{{ selectedOrderNumber || $route.params.orderId }}
        {{ bringgOrderResponse.status }}!
      </h1>
    </div>

    <section
      class="order-confirmed__content-wrapper"
      :style="{'padding-bottom': isBringgOrder ? '0' : '48px'}"
    >
      <section class="order-confirmed__info">
        <h2
          class="line-height-1 order-confirmed__store-name"
          :class="{'more-space': !isBringgOrder && $vuetify.breakpoint.smAndDown}"
        >
          {{ wait(storeInfo, "name") }}
        </h2>

        <StoreAddress class="order-confirmed__store-address" :lines="2" />
        <ConfirmationStatusBringg
          v-if="isBringgOrder"
          :class="orderContainsAlcoholOrTobacco() ? 'order-confirmed__status' : ''"
        />

        <ConfirmationStatusLocal
          v-if="!isBringgOrder && localOrder"
          :class="{'more-space': !isBringgOrder && $vuetify.breakpoint.smAndDown}"
          :is-delivery="!!localOrder.delivery_method_id"
          :is-paid-by-c-c="localOrder.is_authorized"
          :time="twentyFourHourToAmPm(localOrder.chosen_time)"
          :date="renderPickupDate(localOrder.chosen_date)"
        />

        <section
          v-if="($vuetify.breakpoint.smAndDown || isTrackingPopup ) && bringgDeliveryIsActive && isBringgOrder"
          class="order-confirmed__maps__wrapper"
        >
          <iframe
            v-if="showBringgMap()"
            id="nrs-network-google-maps"
            class="order-confirmed__maps"
            :src="bringgTrackingLink"
            frameborder="0"
          ></iframe>
          <GoogleMap
            v-else
            :customer-lat="customerDeliveryLat"
            :customer-lng="customerDeliveryLng"
          />
        </section>

        <div
          v-if="orderContainsAlcoholOrTobacco()"
          class="order-confirmed__id-reminder"
          :class="{'more-space': !isBringgOrder && $vuetify.breakpoint.smAndDown}"
        >
          <v-icon class="mdi-card-account-details-outline">
            mdi-card-account-details-outline
          </v-icon>
          <p class="order-confirmed__id-reminder__text">
            Remember to<br />present your ID
          </p>
        </div>

        <section
          class="order-confirmed__buttons"
          :class="{
            'margin-big': orderContainsAlcoholOrTobacco(),
            'more-space': !isBringgOrder && $vuetify.breakpoint.smAndDown
          }"
        >
          <v-btn
            v-if="showOrderInformationButton()"
            class="order-confirmed__buttons__view-order-button"
            height="48"
            width="180"
            outlined
            @click="seeOrderInformation"
            rounded
            color="primary"
          >
            <div class="font-size-md text-capitalize e2e-received-done">
              View Order
            </div>
          </v-btn>

          <v-btn
            class="order-confirmed__buttons__ok-button"
            height="48"
            width="180"
            @click="clearConfirmedOrderAndRouteHome"
            rounded
            color="primary"
          >
            <div class="font-size-md text-capitalize e2e-received-done">OK</div>
          </v-btn>
        </section>

        <section
          class="order-confirmed__nrs-delivery"
          :class="orderContainsAlcoholOrTobacco() ? '' : 'margin-big'"
          v-if="isBringgOrder"
        >
          <img
            class="order-confirmed__nrs-delivery__image"
            src="@/assets/order_status/nrs_delivery_logo.svg"
            alt="NRS Delivery Network logo"
            height="63"
            width="51"
          />
          <span class="order-confirmed__nrs-delivery__text">
            Powered by NRS Delivery Network
          </span>
        </section>
      </section>

      <section
        v-if="$vuetify.breakpoint.mdAndUp &&
          !isTrackingPopup &&
          bringgDeliveryIsActive &&
          isBringgOrder"
        class="order-confirmed__maps__wrapper"
      >
        <iframe
          v-if="showBringgMap()"
          class="order-confirmed__maps"
          :src="bringgTrackingLink"
          frameborder="0"
        ></iframe>
        <GoogleMap
          v-else
          :confirmed-page-desktop="true"
          :customer-lat="customerDeliveryLat"
          :customer-lng="customerDeliveryLng"
        />
      </section>
    </section>
  </div>
</template>

<script>
import moment from "moment-timezone";
import { mapState } from "vuex";
import { twentyFourHourToAmPm, wait } from "@/util.js";
import StoreAddress from "@/components/TopStrip/StoreAddress.vue";
import ConfirmationStatusBringg from "@/components/Order/Confirmation/ConfirmationStatusBringg.vue";
import ConfirmationStatusLocal from "@/components/Order/Confirmation/ConfirmationStatusLocal.vue";
import orderConfirmationCheck from "@/assets/order_status/orderConfirmationCheck.vue";
import { formatBringgEta, setLocalOrder } from "@/helper";
import GoogleMap from '@/components/Google/GoogleMap.vue';
//local testing
//const delay = (ms) => new Promise((res) => setTimeout(res, ms));

export default {
  name: "ConfirmedPaymentView",
  components: {
    GoogleMap,
    ConfirmationStatusBringg,
    ConfirmationStatusLocal,
    StoreAddress,
    orderConfirmationCheck,
  },
  props: { fromBringgOrder: Boolean, isTrackingPopup: Boolean },
  async beforeMount() {
    this.orderNumber = this.selectedOrderNumber || this.$route.params.orderId;
    await this.$store.dispatch(
      "orderStatus/updateBringgOrderResponse",
      this.orderNumber,
    );
    await this.setLocalOrder();
    await this.$store.dispatch(
      "orderStatus/setLocalOrderAddress",
      this.localOrder.delivery_address.full_address,
    );
    if (this.$route.name !== "account") this.addOrderToOrderHistory();
    const formattedEta = formatBringgEta(
      this.localOrder.bringg_delivery_estimate_local,
    );
    await this.$store.dispatch("orderStatus/setOrderEta", formattedEta, {
      root: true,
    });
    await this.getLatLng();
  },
  async beforeDestroy() {
    this.orderId = 0;
    this.$store.commit("orderStatus/CLEAR");
    clearInterval(this.orderInfoPollingTimeout);
  },
  computed: {
    ...mapState({
      storeInfo: (state) => state.store.info,
      bringgOrderResponse: (state) => state.orderStatus.bringgOrderResponse,
      bringgTrackingLink: (state) => state.orderStatus.bringgTrackingLink,
      selectedOrderNumber: (state) => state.orderStatus.selectedOrderNumber,
    }),
    isBringgOrder() {
      return this.fromBringgOrder || this.localOrder?.delivery_method_id === 3;
    },
    bringgDeliveryIsActive() {
      return this.bringgOrderResponse.status !== "Canceled by Store";
    }
  },
  methods: {
    showBringgMap() {
      if (this.bringgOrderResponse.orderStep !== 3) return false;
      return this.bringgTrackingLink;
    },
    addOrderToOrderHistory() {
      this.$store.commit("orders/ADD_ORDER", this.localOrder);
    },


    setLocalOrder,
    async updateOrderDetails() {
      if (this.orderInfoPollingTimeout) {
        clearInterval(this.orderInfoPollingTimeout);
      }
      if (
        this.bringgOrderResponse.orderStep > 3 ||
        this.bringgOrderResponse.status === "Canceled by Store"
      )
        return;
      let timeoutTime = 0;
      if (
        this.bringgOrderResponse.orderStep === 1 ||
        this.bringgOrderResponse.orderStep === 2
      )
        timeoutTime = 300000;
      if (this.bringgOrderResponse.orderStep === 3) timeoutTime = 90000;
      this.orderInfoPollingTimeout = setInterval(async () => {
        await this.$store.dispatch(
          "orderStatus/updateBringgOrderResponse",
          this.orderNumber,
        );
      }, timeoutTime);
    },
    orderContainsAlcoholOrTobacco() {
      if (!this.localOrder) return false;
      return this.localOrder.details.products.find(
        (product) => product.is_alcohol || product.is_tobacco,
      );
    },
    seeOrderInformation() {
      this.$router.push("/account#orders");
    },
    showOrderInformationButton() {
      return this.$route.name !== "account" && this.$vuetify.breakpoint.mdAndUp;
    },
    clearConfirmedOrderAndRouteHome() {
      if (this.$route.name === "account")
        return this.$store.dispatch("orderStatus/updateCurrentOrderNumber", 0);
      this.$store.commit('orders/SET_INITIAL_ORDER_STATE', this.storeInfo);
      this.$router.push({ name: "home" });
    },
    renderPaymentType() {
      return this.order.noCC ? "Pay in Store" : "Paid with Credit Card";
    },
    renderPickupDate(date) {

      moment.tz.setDefault(this.storeInfo.tz);

      const mom = moment();
      const today = moment(mom.format("YYYY-MM-DD")).isSame(date);
      const tomorrow = moment(mom.add(1, "day").format("YYYY-MM-DD")).isSame(date);
      return today || tomorrow
        ? today
          ? "Today"
          : "Tomorrow"
        : moment(date).format("ddd, MMMM D");
    },
    renderServiceType() {
      return this.order.service === "DELIVERY" ? "Delivery" : "Pick-Up";
    },
    twentyFourHourToAmPm,
    wait,
    async getLatLng() {
      const customerAddress = this.$store.state.orderStatus.localOrderAddress;
      if (!customerAddress) return;
      const geocoder = new window.google.maps.Geocoder();
      const results = await new Promise((resolve, reject) => {
        geocoder.geocode({ address: customerAddress }, (results, status) => {
          if (
            status === window.google.maps.GeocoderStatus.OK &&
            results.length > 0
          ) {
            resolve(results);
          } else {
            reject(new Error(status));
          }
        });
      });
      if (results && results.length > 0) {
        const location = results[0].geometry.location;
        this.customerDeliveryLat = location.lat();
        this.customerDeliveryLng = location.lng();
      }
    },
  },
  data() {
    return {
      orderId: null,
      orderInfoPollingTimeout: null,
      orderNumber: null,
      localOrder: null,
      formattedDeliveryAddress: "",
      customerDeliveryLat: null,
      customerDeliveryLng: null,
    };
  },
  watch: {
    bringgOrderResponse: async function(newVal, oldVal) {
      if (newVal.status === oldVal.status) return;
      await this.updateOrderDetails();
    },
  },
};
</script>

<style lang="scss" scoped>
.order-confirmed {
  width: 100%;
  margin: 0 auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 4px 4px 10px 0 rgba(0, 0, 0, 0.15);
  overflow: hidden;

  &__header {
    margin-top: 0;
    background: var(--v-secondary-base);
    color: #fff;
    height: 80px;
    font-size: 20px;
    display: flex;
    justify-content: center;
    align-items: center;

    &__image {
      margin-right: 18px;
    }

    &__text {
      font-size: 20px;
      font-weight: bold;
    }

    &-completed {
      background: #41b029;
    }
  }

  &__content-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100vh;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 34px;
    width: 100%;

    & .margin-big {
      margin-top: 24px;
    }
  }

  &__store-name {
    font-size: 16px;
    margin-top: 10px;
    padding: 0;
  }

  &__store-address {
    font-weight: normal;
    margin: 8px 0 10px 0;
  }

  &__maps {
    height: 100%;
    width: 100%;

    &__wrapper {
      width: 100%;
      height: 221px;
    }
  }

  &__id-reminder {
    display: flex;
    align-items: center;
    margin-top: 16px;

    &__text {
      text-align: left;
      font-size: 16px;
      color: #212121;
      margin: 0 0 0 20px;
      line-height: 1;
      font-weight: bold;
    }
  }

  &__buttons {
    margin-top: 20px;

    &__ok-button {
      color: #fff;
      background-color: var(--v-primary-base) !important;
    }
  }

  &__nrs-delivery {
    align-items: center;
    margin-top: 32px;
    display: flex;
    justify-content: center;

    &__image {
      width: 12.1px;
      height: 11.7px;
    }

    &__text {
      font-weight: normal;
      font-size: 12px;
    }
  }

  .more-space {
    margin-top: 48px;
  }
}

@media #{map-get($display-breakpoints, 'sm-and-up')} {
  .order-confirmed {
    background: var(--color-seashell);

    &.not-tracking-popup {
      width: 1156px;
      margin-top: 215px;
      margin-bottom: 215px;
    }

    &__header-margin {
    }

    &__store-name {
      margin-top: unset;
      font-size: 22px;
    }

    &__store-address {
      font-size: 16px;
      margin: 8px 0 10px 0;
    }

    &__status {

    }

    &__info {
      flex-direction: column;
      //margin-top: -215px;
      margin-top: 61px;

      & .margin-big {
        margin-top: 70px
      }
    }

    &__id-reminder {

    }

    &__buttons {
      display: flex;
      margin-top: 40px;

      &__view-order-button {
        margin-right: 32px;
      }

      &__ok-button {
        margin-top: 0;
      }
    }

    &__content-wrapper {
      flex-direction: row;
      align-items: flex-start;
      background: #fff;
      height: auto;
    }

    &__maps {
      height: 623px;
      width: 100%;
    }

    &__nrs-delivery {
      position: unset;
    }
  }
}

.mdi-card-account-details-outline {
  color: black;
  font-size: 65px;
}
</style>
